<template>
  <b-sidebar
    id="sidebar-backdrop"
    backdrop-variant="dark"
    backdrop
    shadow
    right
    :width="$globals.view.outerWidth > 1280 ? '30%' : '100%'"
    @change="resetValues"
  >
    <div class="sidebar-content mt-4">
      <div class="accordion" role="tablist">
        <b-card no-body class="mb-1 card-container">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <button v-b-toggle="'accordion-1 accordion-2'" class="btn block w-100 btn-secondary">
              Quick Report
            </button>
          </b-card-header>
          <b-collapse id="accordion-1" visible accordion="my-accordion" role="tabpanel" @show="resetValues">
            <b-card-body>
              <div>
                <h6 class="title">Date Range</h6>
                <header-buttons
                  class="dash-button-group"
                  :from="creditsFrom"
                  :through="creditsThrough"
                  @change="setDateRangeForBasicReport"
                />
                <fragment v-if="customUserTags.hasTags()">
                  <h6 class="title mt-3">Filter By Tag</h6>
                  <custom-tags-input
                    class="w-50"
                    :value="credits.customCampusTags"
                    @input="setCustomTagsForBasicReport"
                  />
                </fragment>
              </div>
              <div class="pt-4">
                <button class="btn download" :disabled="loading" @click="$emit('generate',{})">
                  Download
                  <font-awesome-icon
                    :icon="{
                      prefix:'fas',
                      iconName: generating ? 'spinner' : 'download'
                    }"
                    :spin="generating"
                    :class="{ icon: true, alt: generating }"
                  />
                </button>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>

        <b-card no-body class="mb-1 card-container">
          <b-card-header header-tag="header" class="p-1" role="tab">
            <button
              v-b-toggle="'accordion-1 accordion-2'"
              class=" btn block w-100 btn-secondary"
            >
              Customizable Report
            </button>
          </b-card-header>
          <b-collapse id="accordion-2" accordion="my-accordion" role="tabpanel" @show="resetValues">
            <b-card-body class="advanced-card">
              <div>
                <h3 class="title">Credits Usage</h3>
                <h6 class="title">Date Range</h6>
                <header-buttons
                  class="dash-button-group"
                  :from="creditsFrom"
                  :through="creditsThrough"
                  @change="handleCreditDateChange"
                />
                <fragment v-if="customUserTags.hasTags()">
                  <h6 class="title mt-3">Filter Credit Usage By Tag</h6>
                  <custom-tags-input
                    class="w-50"
                    :value="credits.customCampusTags"
                    @input="setCustomCreditsTags"
                  />
                </fragment>
              </div>
              <div class="mt-4">
                <h3 class="title">Registrations</h3>
                <h6 class="title">Date Range</h6>
                <header-buttons
                  class="dash-button-group"
                  :from="registrationsFrom"
                  :through="registrationsThrough"
                  @change="handleRegistrationsDateChange"
                />
                <div class="mt-3">
                  <h6 class="title">View Registrations by </h6>
                  <b-form-select
                    class="w-50"
                    :value="registrationsBinBy"
                    :options="[
                      { value: 'year', text: 'Year' },
                      { value: 'quarter', text: 'Quarter' },
                      { value: 'month', text: 'Month' },
                      { value: 'week', text: 'Week' },
                      { value: 'day', text: 'Day' },
                    ]"
                    @input="setRegistrationsBinBy"
                  >
                  </b-form-select>
                </div>
                <fragment v-if="customUserTags.hasTags()">
                  <h6 class="title mt-3">Filter Registrations By Tag</h6>
                  <custom-tags-input
                    class="w-50"
                    :value="registrationsReport.parameters.customCampusTags"
                    @input="setCustomRegistrationTags"
                  />
                </fragment>
              </div>
              <div class="pt-4">
                <button class="btn download" :disabled="loading" @click="$emit('generate',{})">
                  Download
                  <font-awesome-icon
                    :icon="{
                      prefix:'fas',
                      iconName: generating ? 'spinner' : 'download'
                    }"
                    :spin="generating"
                    :class="{ icon: true, alt: generating }"
                  />
                </button>
              </div>
            </b-card-body>
          </b-collapse>
        </b-card>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import HeaderButtons from './Tabs/Credits/components/HeaderButtons.vue';
import CustomTagsInput from './CustomTagsInput.vue';
import moment from 'moment';

export default {
    name: 'ShareSidebar',
    components: {
        HeaderButtons,
        CustomTagsInput,
    },
    props: {
        generating: {
            type: Boolean,
            required: true
        }
    },
    inject: [
      'credits',
      'demographicsReport',
      'registrationsReport',
      'customUserTags'
    ],

    computed:{
        loading() {
            return this.generating || this.registrationsReport.loading || this.credits.loading;
        },
        creditsFrom() {
            return this.credits.from ? this.credits.from.format('YYYY-MM-DD') : null;
        },
        creditsThrough() {
            return this.credits.through ? this.credits.through.format('YYYY-MM-DD') : null;
        },
        registrationsFrom() {
            return this.registrationsReport.parameters.from ? moment(this.registrationsReport.parameters.from).format('YYYY-MM-DD') : null;
        },
        registrationsThrough() {
            return this.registrationsReport.parameters.through ? moment(this.registrationsReport.parameters.through).format('YYYY-MM-DD') : null;
        },
        registrationsBinBy() {
            return this.registrationsReport.parameters.binBy;
        },
  },
  methods: {
    resetValues() {
      this.setCustomTagsForBasicReport([]);
      this.setDateRangeForBasicReport({ selected: 'all', from: null, through: moment().endOf('day').toISOString() });
    },
    setCustomTagsForBasicReport(tagsList) {
        this.setCustomCreditsTags(tagsList);
        this.setCustomRegistrationTags(tagsList);
    },
    setDateRangeForBasicReport({ selected, from, through }) {
        this.handleCreditDateChange({ from, through });
        this.handleRegistrationsDateChange({ from, through });
        const binBy = this.getRegistrationBinForBasicReport(selected, from, through)
        this.setRegistrationsBinBy(binBy);
    },
    getRegistrationBinForBasicReport(selected, from, through) {
      if (selected === 'all') return 'year';
      if (selected === 'recent') return 'week';

      through = moment(through);

      if (!from) return 'year';

      from = moment(from);

      const monthsDiff = through.diff(from, 'months');
      const daysDiff = through.diff(from, 'days');

      if (monthsDiff > 12) return 'year';
      if (monthsDiff > 3) return 'quarter';
      if (monthsDiff > 1) return 'month';
      if (daysDiff > 7) return 'week';

      return 'day';
    },
    handleCreditDateChange({ from, through }) {
      return this.credits
        .between(from, through)
        .fetchAll()
    },
    handleRegistrationsDateChange({ from, through }) {
        this.registrationsReport.setParameters({ from: from ? moment(from).format('YYYY-MM-DD') : null });
        this.registrationsReport.setParameters({ through: through ? moment(through).format('YYYY-MM-DD') : null });
    },
    setRegistrationsBinBy(e) {
      this.registrationsReport.setParameters({ binBy: e });
    },
    setCustomCreditsTags(tagsList) {
      this.credits.withCustomTags(tagsList);
      this.credits.fetchAll();
    },
    setCustomRegistrationTags(tagsList) {
      this.registrationsReport.setParameters({ customCampusTags: tagsList });
      this.demographicsReport.withCustomTags(tagsList);
    },
  }
}
</script>

<style lang="scss" scoped>
.sidebar-content {
    .accordion {
      .card-container {
          overflow :visible;
        }
        .btn-secondary:focus {
            background-color: $dark-blue;
        }
    }
    .title {
      color: $dark-blue;
      font-family: 'Open Sans', sans-serif;
    }
    .download {
        @include blue-gradient;
        color: #fff;
        width: 50%;
    }
}
</style>
